import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import minMax from "dayjs/plugin/minMax";
import { backendService } from "./BackendDataFetcher.js";
import { harvestDataFilter } from "./HarvestDataFilter.js";
import { forecastService } from "./ForecastDataFetcher.js";
import { forecastFillService } from "./Forecast.js";
import { harvestAllocatorService } from "./HarvestAllocator.js";
import { rowLengthService } from "./RowLengthService.js";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";

dayjs.extend(minMax);

/** @typedef {import("./harvestOptimizer.types.d.ts").HarvestTask} HarvestTask */
/** @typedef {import("./harvestOptimizer.types.d.ts").RecommendedScheduleProcessorProps} RecommendedScheduleProcessorProps */

const addOrdinalSuffix = (num) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const value = num % 100;
  return `${num}${suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]}`;
};

export const formatDayHeading = (dateObj) =>
  `${dateObj.format("dddd")} (${addOrdinalSuffix(dateObj.date())} ${dateObj.format("MMM")})`;

const buildDateArrayInclusive = (startISO, endISO) => {
  const start = dayjs(startISO).startOf("day");
  const end = dayjs(endISO).startOf("day");
  const days = [];
  for (
    let cursor = start;
    !cursor.isAfter(end, "day");
    cursor = cursor.add(1, "day")
  ) {
    days.push(cursor);
  }
  return days;
};

const buildWeekMondayToSunday = (selectedDate) => {
  const dayOfWeek = selectedDate.day();
  const monday = selectedDate
    .subtract((dayOfWeek + 6) % 7, "day")
    .startOf("day");
  return Array.from({ length: 7 }, (_, index) => monday.add(index, "day"));
};

const filterDatesByVisibleNames = (dates, visibleDaysConfig) => {
  const wanted = new Set(
    Object.keys(visibleDaysConfig || {}).map((n) => n.toLowerCase()),
  );
  return dates.filter((d) => wanted.has(d.format("dddd").toLowerCase()));
};

const toISO = (d) => {
  if (!d) return "";
  if (dayjs.isDayjs(d)) return d.format(BACKEND_DATE_FORMAT);

  // Try strict parse for known backend format first, else fall back to dayjs parse
  const strict = dayjs(d, BACKEND_DATE_FORMAT, true);
  const parsed = strict.isValid() ? strict : dayjs(d);

  return parsed.isValid() ? parsed.format(BACKEND_DATE_FORMAT) : "";
};

const shiftISO = (iso, days) =>
  dayjs(iso).add(days, "day").format(BACKEND_DATE_FORMAT);
const getId = (x) => (x && typeof x === "object" ? x.id : x);
const hashVisibleDays = (cfg) => {
  const entries = Object.entries(cfg || {}).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  return entries
    .map(([k, v]) => {
      if (v && typeof v === "object") {
        const t = String(v.type);
        const val = Number(v.value) || 0;
        return `${k}:${t}:${val}`;
      }
      // legacy numeric case
      return `${k}:${Number(v) || 0}`;
    })
    .join("|");
};

const sortByDate = (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0);

function computeForecastWindow({
  unharvestedData,
  selectedISO,
  endISO,
  horizonDays,
}) {
  const sel = dayjs(selectedISO, BACKEND_DATE_FORMAT, true);
  const end = dayjs(endISO, BACKEND_DATE_FORMAT, true);

  let minFirst = null;
  let maxLast = null;

  for (const row of unharvestedData || []) {
    // points with finite leaf size up to selected date
    const pts = (row.points || [])
      .filter(
        (p) =>
          Number.isFinite(p?.avgLeafCm) &&
          dayjs(p.date).isSameOrBefore(sel, "day"),
      )
      .sort(sortByDate);

    if (pts.length < 2) continue;

    const firstISO = pts[0].date;
    const lastISO = pts[pts.length - 1].date;

    if (!minFirst || dayjs(firstISO).isBefore(minFirst))
      minFirst = dayjs(firstISO);
    if (!maxLast || dayjs(lastISO).isAfter(maxLast)) maxLast = dayjs(lastISO);
  }

  // If nothing suitable, fall back to a tiny window (selected..endISO)
  if (!minFirst || !maxLast) {
    return { startISO: toISO(sel), endISO: toISO(end) };
  }

  const endNeeded = dayjs.min(end, maxLast.add(horizonDays, "day"));
  return { startISO: toISO(minFirst), endISO: toISO(endNeeded) };
}

/** ---------- main builder ---------- */
async function buildPlannedHarvest({
  orgId,
  currentSite,
  currentCrop,
  baseDates, // Array<dayjs>
  selectedDate, // dayjs
  visibleDaysConfig, // Record<string, number>
  siteMetadata,
  forecastDays,
  enableTimeMachineForPastDates,
}) {
  if (!baseDates?.length) return [];

  const startISO = toISO(baseDates[0]);
  const endISO = toISO(baseDates[baseDates.length - 1]);
  const widenedStartISO = shiftISO(startISO, -3 * forecastDays);
  const siteId = getId(currentSite);
  const cropId = getId(currentCrop);

  const selectedISO = toISO(selectedDate);
  const backendEndISO = enableTimeMachineForPastDates ? selectedISO : endISO;

  const backendData = await backendService.getFilteredGreenhouse({
    orgId,
    siteId,
    cropId,
    start: widenedStartISO,
    end: backendEndISO,
    forecastDays,
  });
  // console.log("Backend Data", backendData);
  const filteredData = harvestDataFilter.getFilteredData({
    selectedDate: selectedISO,
    backendData,
    startISO,
    endISO,
    forecastDays,
    enableTimeMachineForPastDates,
  });

  // console.log("filteredData", filteredData);

  const { lat, long } = currentSite;
  const latLong = `${lat},${long}`;

  const { startISO: tempStartISO, endISO: tempEndISO } = computeForecastWindow({
    unharvestedData: filteredData.unharvestedData,
    selectedISO,
    endISO,
    horizonDays: forecastDays,
  });

  const forecastData = await forecastService.getForecast({
    latLong,
    selectedISO,
    endISO,
    historyYears: 0,
    horizonDays: 0,
    startOverrideISO: tempStartISO,
    endOverrideISO: tempEndISO,
    siteMetadata,
  });

  // console.log("forecastData", forecastData);
  const updatedUnharvestedData = forecastFillService({
    unharvestedData: filteredData.unharvestedData,
    forecastData: forecastData.temps,
    forecastDays: forecastDays,
    gddBase: siteMetadata?.GDD_BASE_TEMPERATURE_IN_CELSIUS ?? 5,
    startISO,
    endISO,
    selectedDate,
  });
  // console.log("Forecast Fill", updatedUnharvestedData);

  const updatedUnharvestedWithRowLength = rowLengthService.addRowLength({
    data: updatedUnharvestedData,
    siteMetadata,
  });

  const updatedHarvestedDataWithRowLength = rowLengthService.addRowLength({
    data: filteredData.harvestedData,
    siteMetadata,
  });

  console.log(
    "Updated Unharvested with Row Length",
    updatedUnharvestedWithRowLength,
  );

  let finalHarvestData = harvestAllocatorService.getFinalData({
    updatedUnharvestedData: updatedUnharvestedWithRowLength,
    harvestedData: updatedHarvestedDataWithRowLength,
    dateWindow: { startISO, endISO },
    selectedDate: selectedISO,
    visibleDaysConfig,
    siteMetadata,
    enableTimeMachineForPastDates,
  });

  // console.log("Final data", finalHarvestData);
  return Array.isArray(finalHarvestData) ? finalHarvestData : [];
}

/**
 * Props:
 * - currentOrg, currentSite, currentCrop
 * - selectedDate: dayjs()
 * - dateRange?: { startISO, endISO } | null
 * - visibleDaysConfig: Record<string, number>
 * - children: ({ loading, error, items }) => ReactNode
 */
const RecommendedSchedulePlanner = ({
  currentOrg,
  currentSite,
  currentCrop,
  selectedDate,
  dateRange,
  visibleDaysConfig,
  siteMetadata,
  children,
  refreshKey = 0,
}) => {
  const FORECAST_DAYS = 7;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  /** @type {[HarvestTask[], any]} */
  const [items, setItems] = useState([]);

  const [enableTimeMachineForPastDates, setEnableTimeMachineForPastDates] =
    useState(false);

  useEffect(() => {
    const v =
      siteMetadata?.HARVEST_OPTIMIZER?.ENABLE_TIME_MACHINE_FOR_PAST_DATES;
    setEnableTimeMachineForPastDates(typeof v === "boolean" ? v : false);
  }, [siteMetadata]);

  // 1) base window: week or explicit range
  const { startISO, endISO, baseDates } = useMemo(() => {
    const dates = dateRange
      ? buildDateArrayInclusive(dateRange.startISO, dateRange.endISO)
      : buildWeekMondayToSunday(selectedDate);
    return {
      startISO: dates.length ? toISO(dates[0]) : null,
      endISO: dates.length ? toISO(dates[dates.length - 1]) : null,
      baseDates: dates,
    };
  }, [selectedDate, dateRange?.startISO, dateRange?.endISO]);

  // 2) filter to visible day names only
  const visibleKey = useMemo(
    () => hashVisibleDays(visibleDaysConfig),
    [visibleDaysConfig],
  );
  const selectedDates = useMemo(
    () => filterDatesByVisibleNames(baseDates, visibleDaysConfig),
    [baseDates, visibleKey],
  );

  useEffect(() => {
    let alive = true;

    (async () => {
      const orgId = getId(currentOrg);
      const siteId = getId(currentSite);
      const cropId = getId(currentCrop);

      if (!orgId || !siteId || !cropId || !startISO || !endISO) {
        if (alive) {
          setItems([]);
          setError("");
          setLoading(false);
        }
        return;
      }

      // Build a stable run key based on values that must trigger a refetch

      setLoading(true);
      setError("");

      try {
        const finalHarvestData = await buildPlannedHarvest({
          orgId,
          currentSite,
          currentCrop,
          baseDates,
          selectedDate,
          visibleDaysConfig,
          siteMetadata,
          forecastDays: FORECAST_DAYS,
          enableTimeMachineForPastDates,
        });
        if (alive) setItems(finalHarvestData);
      } catch (e) {
        if (alive) setError(e?.message || "Planner pipeline failed");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [
    currentOrg,
    currentSite,
    currentCrop,
    startISO,
    endISO,
    visibleKey,
    selectedDate,
    baseDates.length,
    refreshKey,
    siteMetadata,
    enableTimeMachineForPastDates,
  ]);

  return children({ loading, error, items });
};

export default RecommendedSchedulePlanner;
